import { FunctionComponent, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Grid } from '@material-ui/core';

import BrandSection from './BrandSection';
import CampaignAttributesSection from './CampaignAttributesSection';
import CampaignDetailsSection from './CampaignDetailsSection';
import OtherResponsiblePartiesSection from './OtherResponsiblePartiesSection';
import SampleMessagesSection from './SampleMessagesSection';
import SampleMultimediaSection from './SampleMultimediaSection';
import SupportingDocumentSection from './SupportingDocumentSection';
import {
  CampaignDetailsFormValues,
  FileFormats,
  MnoMetadata,
  Reseller,
} from '../../types';
import { Cnp, UsecaseType } from '@/utils/types';

interface CampaignDetailsFormProps {
  greyOutFields: string[];
  supportedFileFormatsForSupportingDocument: FileFormats;
  supportedFileFormatsForSampleMultimedia: FileFormats;
  mnos: MnoMetadata[];
  termsAndConditions: string[];
  cnps: Cnp[];
  resellers: Reseller[];
  brandName: string;
  useCase: UsecaseType;
  defaultValues?: CampaignDetailsFormValues;
  onChange?: (values: CampaignDetailsFormValues) => void;
  onNewResellerRequest?: () => void;
  onPrevious?: () => void;
  onNext?: (values: CampaignDetailsFormValues) => void;
}

const CampaignDetailsForm: FunctionComponent<CampaignDetailsFormProps> = ({
  greyOutFields,
  supportedFileFormatsForSupportingDocument,
  supportedFileFormatsForSampleMultimedia,
  mnos,
  termsAndConditions,
  cnps,
  resellers,
  brandName,
  useCase,
  defaultValues,
  onChange,
  onNewResellerRequest,
  onPrevious,
  onNext,
}) => {
  const form = useForm<CampaignDetailsFormValues>({
    mode: 'onTouched',
    defaultValues,
  });
  const {
    formState: { isSubmitting, errors },
    getValues,
    setValue,
    handleSubmit,
    watch,
  } = form;
  const minimumSampleMessages = useMemo(
    () => Math.max(...mnos.map((mno) => mno.minMsgSamples)),
    [mnos]
  );

  const handleValid = (values: CampaignDetailsFormValues) => {
    onNext?.(values);
  };

  // For setting new reseller after created during campaign registration
  useEffect(() => {
    const newResellerUid = defaultValues?.resellerUid;
    if (newResellerUid) {
      const resellerUid = getValues('resellerUid');
      if (newResellerUid !== resellerUid) {
        setValue('resellerUid', newResellerUid, { shouldValidate: true });
      }
    }
  }, [defaultValues?.resellerUid]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      onChange?.(value as CampaignDetailsFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={handleSubmit(handleValid)}>
        <Grid container spacing={0} alignItems="center">
          <BrandSection brandName={brandName} />
          <CampaignDetailsSection
            useCase={useCase}
            greyOutFields={greyOutFields}
          />
          <SupportingDocumentSection
            greyOutFields={greyOutFields}
            supportedFileFormats={supportedFileFormatsForSupportingDocument}
          />
          <SampleMessagesSection
            greyOutFields={greyOutFields}
            minimumCount={minimumSampleMessages}
          />
          <SampleMultimediaSection
            greyOutFields={greyOutFields}
            supportedFileFormats={supportedFileFormatsForSampleMultimedia}
          />
          <CampaignAttributesSection
            greyOutFields={greyOutFields}
            mnos={mnos}
            termsAndConditions={termsAndConditions}
          />
          <OtherResponsiblePartiesSection
            cnps={cnps}
            resellers={resellers}
            onNewResellerRequest={onNewResellerRequest}
          />
        </Grid>
        <Grid item xs={12} className="bottom-blk">
          <ul className="list-inline">
            <li className="">
              <p>
                <a
                  data-testid="campaignRegisterFormPreviousButton"
                  className={
                    isSubmitting ||
                    Object.values(errors).some((error) => !!error?.message)
                      ? 'secondary-btn disabled'
                      : 'secondary-btn'
                  }
                  onClick={onPrevious}
                >
                  Previous
                </a>
              </p>
            </li>
            <li className="form-group-field">
              <input
                data-testid="campaignRegisterFormNextButton"
                type="submit"
                disabled={
                  isSubmitting ||
                  Object.values(errors).some((error) => !!error?.message)
                }
                value={isSubmitting ? 'Processing..' : 'Next'}
              />
            </li>
          </ul>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default CampaignDetailsForm;
